import { useState, useEffect } from "react";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { formatNZDateTime } from "../../utils/timezone";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Mail, RotateCw, Play } from "lucide-react";

const EMAIL_TYPE_LABELS = {
  request_expired: "Request Expired",
  quote_expiring_today: "Quote Expiry Reminder",
};

const EMAIL_TYPE_COLORS = {
  request_expired: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  quote_expiring_today: "bg-blue-500/10 text-blue-600 border-blue-500/30",
};

export function AdminEmailLogsTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-logs?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data);
    } catch {
      toast.error("Failed to load email logs");
    } finally {
      setLoading(false);
    }
  };

  const triggerEmails = async () => {
    setTriggering(true);
    try {
      await axios.post(`${API}/admin/trigger-expiry-emails`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Expiry email job triggered — check logs");
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to trigger emails");
    } finally {
      setTriggering(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Card className="glass-card" data-testid="email-logs-tab">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Automated Email Reminders
            </CardTitle>
            <CardDescription>Expiry and reminder emails sent daily at 7:00 AM NZ time</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading}>
              <RotateCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
            </Button>
            <Button size="sm" onClick={triggerEmails} disabled={triggering} data-testid="trigger-expiry-emails-btn">
              <Play className="h-4 w-4 mr-1" />{triggering ? "Running..." : "Run Now"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && logs.length === 0 ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
        ) : logs.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No reminder emails sent yet. The scheduler runs daily at 7:00 AM NZ time.</p>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sent At</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Request</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log, i) => (
                <TableRow key={`${log.request_id}-${log.sent_at}-${i}`}>
                  <TableCell className="text-sm">{formatNZDateTime(log.sent_at)}</TableCell>
                  <TableCell>
                    <Badge className={EMAIL_TYPE_COLORS[log.email_type] || "bg-gray-500/10 text-gray-600"}>
                      {EMAIL_TYPE_LABELS[log.email_type] || log.email_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{log.request_id}</TableCell>
                  <TableCell className="text-sm">{log.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{log.customer_email}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
